import { ChangeEvent, FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Button, Dropdown, Input, Loading, ReportCard, Section, Table } from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import { exportSalesReportCsv, fetchSalesReport, fetchSalesResum } from '../../../../module/company/repository/ComapnyRepository';
import {
	TConcessionaria,
	TGrupoConcessionaria,
	getAllAdminDealers,
	getAllGroupDealers,
} from '../../../../services';
import { useToastStore } from '../../../../stores';
import { formataPlaca, moneyFormatter } from '../../../../utils';
import {
	Form,
	FullLine,
	HalfLine,
	HalfLineButton,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

interface ISalesVehicle {
	placa: string;
	veiculo: string;
}

interface ISalesReportItem {
	id_anuncio: number;
	dat_cadastro: string;
	concessionaria: string;
	revenda: string;
	veiculo: ISalesVehicle[];
	dat_venda: string;
	val_venda: number;
	qtd_total_click: number;
	time_sell: number;
}

const AdminSellerReport: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [filteredOffers, setFilteredOffers] = useState<ISalesReportItem[]>([]);
	const [totalSales, setTotalSales] = useState(0);
	const [placa, setPlaca] = useState('');
	const [startSellDate, setStartSellDate] = useState('');
	const [endSellDate, setEndSellDate] = useState('');
	const [graphLineData, setGraphLineData] = useState<IGraphLineData[]>([]);
	const [gruposConcessionarias, setGruposConcessionarias] = useState<TGrupoConcessionaria[]>([]);
	const [filteredConcessionarias, setFilteredConcessionarias] = useState<TConcessionaria[]>([]);
	const [clickGenerate, setClickGenerate] = useState(false);
	const [totalViews, setTotalViews] = useState(0);
	const [averageSellTime, setAverageSellTime] = useState('--:--');
	const [totalVehiclesSold, setTotalVehiclesSold] = useState(0);


	const grupoOptions = useMemo(() => {
		const options =
			gruposConcessionarias
				.map((item) => ({
					value: item.id.toString(),
					label: item.grupoConcessionaria || '',
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [];
		options.unshift({ value: '0', label: 'Todos os Grupos' });
		return options;
	}, [gruposConcessionarias]);

	const [selectedGroupOption, setSelectedGroupOption] = useState<Option>(
		grupoOptions[0] || { value: '0', label: 'Nenhum grupo de concessionária relacionado' }
	);

	const dealerOptions = useMemo(() => {
		const options =
			filteredConcessionarias
				.map((item) => ({
					value: item.id.toString(),
					label: item.fantasia || '',
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [];
		options.unshift({ value: '0', label: 'Todas as Concessionárias' });
		return options;
	}, [filteredConcessionarias]);

	const [selectedOption, setSelectedOption] = useState<Option>(
		dealerOptions[0] || { value: '0', label: 'Nenhuma concessionária relacionada' }
	);

	const handleSelectOption = (option: Option) => {
		setSelectedOption(option);
	};

	const handleSelectGroupOption = (option: Option) => {
		setSelectedGroupOption(option);
	};

	const handleSetPlaca = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { value } = e.target;
		const newPlaca = formataPlaca(value);
		setPlaca(newPlaca)
	};

	useLayoutEffect(() => {
		const fetchDealerGroups = async () => {
			setLoading(true);
			try {
				const response = await getAllGroupDealers();
				setGruposConcessionarias(response || []);
			} catch {
				setToast({ message: 'Erro ao buscar grupos de concessionária', variant: 'error' });
			} finally {
				setLoading(false);
			}
		};

		const fetchAllDealers = async () => {
			setLoading(true);
			try {
				const response = await getAllAdminDealers();
				setFilteredConcessionarias(response || []);
			} catch {
				setToast({ message: 'Erro ao buscar as concessionárias!', variant: 'error' });
			} finally {
				setLoading(false);
			}
		};

		fetchDealerGroups();
		fetchAllDealers();
	}, [setToast]);

	const formatMinutesToTime = (totalMinutes: number): string => {
		const h = Math.floor(totalMinutes / 60);
		const m = totalMinutes % 60;
		return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
	};

	const fetchData = async () => {
		if (startSellDate && endSellDate && endSellDate >= startSellDate) {
			setLoading(true);

			const grupoId = selectedGroupOption.value !== '0' ? selectedGroupOption.value.toString() : null;
			const concessionariaId = selectedOption.value !== '0' ? selectedOption.value.toString() : null;

			const adjustedEndDate = new Date(endSellDate + 'T00:00:00');
			adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
			const formattedEndDate = adjustedEndDate.toISOString().split('T')[0];

			const [reportResponse, resumResponse] = await Promise.all([
				fetchSalesReport(startSellDate, formattedEndDate, grupoId, concessionariaId),
				fetchSalesResum(startSellDate, formattedEndDate, grupoId, concessionariaId),
			]);

			if (!reportResponse?.success || !resumResponse?.success) {
				setToast({ variant: 'error', message: reportResponse?.message || resumResponse?.message || 'Erro ao buscar dados!' });
				setLoading(false);
				return;
			}

			const details: ISalesReportItem[] = reportResponse.data;
			const summary = resumResponse.data;
			const salesCountByMonthYear: IGraphLineData[] = [];

			const filtered = details.filter((item) => {
				return !placa || item.veiculo?.some(v => v.placa.toUpperCase().includes(placa.toUpperCase()));
			}).map((item) => {
				const dateVenda = new Date(item.dat_venda);
				const label = `${dateVenda.getMonth() + 1}/${dateVenda.getFullYear()}`;
				const existingEntry = salesCountByMonthYear.find((entry) => entry.label === label);

				if (existingEntry) {
					existingEntry.qtde++;
				} else {
					salesCountByMonthYear.push({ label, qtde: 1 });
				}

				return item;
			});

			setGraphLineData(salesCountByMonthYear);
			setFilteredOffers(filtered);

		setTotalSales(summary.total_vendas);
		setTotalViews(summary.qtd_views);
		setAverageSellTime(formatMinutesToTime(summary.tempo_medio_venda));
		setTotalVehiclesSold(summary.qtd_veiculo_vendido);

			setClickGenerate(true);
			setLoading(false);
		}
	};

	const tableColumns = useMemo(
		() => [
			{ id: 'registerDate', label: 'Data de cadastro', width: '100px' },
			{ id: 'dealer', label: 'Anunciante' },
			{ id: 'buyer', label: 'Comprador' },
			{ id: 'plate', label: 'Placa' },
			{ id: 'vehicle', label: 'Veículo' },
			{ id: 'sellDate', label: 'Data de Venda', width: '100px' },
			{ id: 'value', label: 'Valor' },
			{ id: 'views', label: 'Views' },
			{ id: 'averageTime', label: 'Tempo de Venda' },
		],
		[]
	);

	const rowMapper = useCallback(
		(item: ISalesReportItem) => {
			const veiculos = item.veiculo || [];
			const qtdExtra = veiculos.length > 1 ? ` (+${veiculos.length - 1})` : '';
			const firstPlaca = veiculos[0]?.placa || '-';
			const firstVeiculo = veiculos[0]?.veiculo || '-';

			return {
			vehicle: {
				value: `${firstVeiculo}${qtdExtra}`,
				rawValue: firstVeiculo,
			},
			plate: {
				value: `${firstPlaca}${qtdExtra}`,
				rawValue: firstPlaca,
			},
			dealer: {
				value: item.concessionaria,
				rawValue: item.concessionaria,
			},
			buyer: {
				value: item.revenda,
				rawValue: item.revenda,
			},
			registerDate: {
				value: new Date(item.dat_cadastro).toLocaleDateString('pt-BR'),
				rawValue: new Date(item.dat_cadastro).getTime(),
			},
			sellDate: {
				value: new Date(item.dat_venda).toLocaleDateString('pt-BR'),
				rawValue: new Date(item.dat_venda).getTime(),
			},
			value: {
				value: moneyFormatter(item.val_venda),
				rawValue: item.val_venda,
			},
			views: {
				value: item.qtd_total_click || 0,
				rawValue: item.qtd_total_click || 0,
			},
			averageTime: {
				value: formatMinutesToTime(item.time_sell),
				rawValue: item.time_sell,
			},
		};
		},
		[]
	);

	const handleExportCsv = useCallback(async () => {
		if (!startSellDate || !endSellDate) {
			setToast({ variant: 'error', message: 'Selecione as datas para exportar.' });
			return;
		}

		setLoading(true);
		const grupoId = selectedGroupOption.value !== '0' ? selectedGroupOption.value.toString() : null;
		const concessionariaId = selectedOption.value !== '0' ? selectedOption.value.toString() : null;

		const result = await exportSalesReportCsv(startSellDate, endSellDate, grupoId, concessionariaId);

		if (!result.success) {
			setToast({ variant: 'error', message: result.message || 'Erro ao exportar CSV.' });
		}
		setLoading(false);
	}, [startSellDate, endSellDate, selectedGroupOption, selectedOption, setToast]);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredOffers?.map(rowMapper)} paginate exportToPDF exportToCSV onExportCSV={handleExportCsv} />,
		[filteredOffers, rowMapper, tableColumns, handleExportCsv]
	);

	return (
		<Section>
			<TitleContainer>Relatório de Vendas</TitleContainer>
			<Separator />
			<Form>
				<HalfLine>
					<Dropdown
						options={grupoOptions}
						selected={selectedGroupOption}
						onSelect={handleSelectGroupOption}
					/>
				</HalfLine>
				<HalfLine>
					<Dropdown
						options={dealerOptions}
						selected={selectedOption}
						onSelect={handleSelectOption}
						searchable
					/>
				</HalfLine>
				<HalfLine>
					<Input
						placeholder="Placa"
						type="text"
						variant='text'
						value={placa}
						onChange={handleSetPlaca}
						maxLength={8}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Venda Inicial*"
						type="date"
						variant='text'
						value={startSellDate}
						onChange={(e) => setStartSellDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Venda Final*"
						type="date"
						variant='text'
						value={endSellDate}
						onChange={(e) => setEndSellDate(e.target.value)}
						min={startSellDate}
					/>
				</HalfLine>
				<HalfLineButton>
					<FullLine>
						<HalfLineButton>
							<Button
								variant='large'
								fitContent
								onClick={fetchData}
								disabled={(!startSellDate || !endSellDate) && !placa}
							>
								Gerar
							</Button>
						</HalfLineButton>
					</FullLine>
				</HalfLineButton>
			</Form>
			{loading && <Loading />}
			{clickGenerate && (
				<ReportCard
					data={[
					{
						color: '#4CAF50',
						title: 'Total de Vendas no Período',
						value: totalSales,
					},
					{
						color: '#f44336',
						title: 'Tempo Médio de Venda',
						value: averageSellTime,
					},
				{
					color: '#2196F3',
					title: 'Views',
					value: totalViews.toLocaleString('pt-BR'),
				},
				{
					color: '#FF9800',
					title: 'Veículos Vendidos',
					value: totalVehiclesSold,
				},
				]}
				/>
			)}
			<TableContainer>
				{(loading || (filteredOffers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && filteredOffers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
				{graphLineData.length > 0 && (
					<FullLine>
						<Line
							data={{
								labels: graphLineData.map((item) => item.label),
								datasets: [
									{
										label: 'Vendas por período',
										data: graphLineData.map((item) => item.qtde),
										backgroundColor: 'rgba(255, 102, 0, 0.5)',
										borderColor: 'rgba(255, 102, 0)',
									},
								],
							}}
						/>
					</FullLine>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminSellerReport;
